/** Bits used to track the dirty state of an FsNode. */
export enum DirtyBit {
  /** A constant used to indicate that the node is clean. */
  None = 0,
  /** Some descendant of this node is dirty. */
  Descendants = 1 << 0,
  /** The child list of this node is dirty. */
  ChildList = 1 << 1,
  /** This node is new and may be dirty because it hasn't been flushed before. */
  NewlyAttached = 1 << 2,
  /** The shadow root of this node is dirty. */
  ShadowRoot = 1 << 3,
  /** The helper inline styles applied to this node are dirty. */
  HelperInlineStyle = 1 << 4,
  /** The stylesheet attached to this <style> or <link> node is dirty. */
  StyleSheet = 1 << 5,
  /** The adopted stylesheets attached to this #document node is dirty. */
  AdoptedStyleSheets = 1 << 6,
  /** The scroll position of this node is dirty. */
  ScrollPosition = 1 << 7,
  /** The text content of this node is dirty. */
  Text = 1 << 8,
  /** The value of this input-like node is dirty. */
  Value = 1 << 9,
  /** The play/pause state of this media-like node is dirty. */
  PlayPause = 1 << 10,
  /** Canvas operations to this node is dirty */
  Canvas = 1 << 11,
  /** Property on this node is dirty */
  Property = 1 << 12,
}

/**
 * @returns a string representation of the provided bitfield of DirtyBit values,
 * suitable for debugging.
 */
export function dirtyBitsToString(inBits: number): string {
  let bits = inBits; // for eslint.
  const setBits: string[] = [];
  if (bits & DirtyBit.Descendants) {
    bits = bits & ~DirtyBit.Descendants;
    setBits.push('Descendants');
  }
  if (bits & DirtyBit.ChildList) {
    bits = bits & ~DirtyBit.ChildList;
    setBits.push('ChildList');
  }
  if (bits & DirtyBit.NewlyAttached) {
    bits = bits & ~DirtyBit.NewlyAttached;
    setBits.push('NewlyAttached');
  }
  if (bits & DirtyBit.ShadowRoot) {
    bits = bits & ~DirtyBit.ShadowRoot;
    setBits.push('ShadowRoot');
  }
  if (bits & DirtyBit.HelperInlineStyle) {
    bits = bits & ~DirtyBit.HelperInlineStyle;
    setBits.push('HelperInlineStyle');
  }
  if (bits & DirtyBit.StyleSheet) {
    bits = bits & ~DirtyBit.StyleSheet;
    setBits.push('StyleSheet');
  }
  if (bits & DirtyBit.AdoptedStyleSheets) {
    bits = bits & ~DirtyBit.AdoptedStyleSheets;
    setBits.push('AdoptedStyleSheets');
  }
  if (bits & DirtyBit.ScrollPosition) {
    bits = bits & ~DirtyBit.ScrollPosition;
    setBits.push('ScrollPosition');
  }
  if (bits & DirtyBit.Text) {
    bits = bits & ~DirtyBit.Text;
    setBits.push('Text');
  }
  if (bits & DirtyBit.Value) {
    bits = bits & ~DirtyBit.Value;
    setBits.push('Value');
  }
  if (bits & DirtyBit.PlayPause) {
    bits = bits & ~DirtyBit.PlayPause;
    setBits.push('PlayPause');
  }
  if (bits !== 0) {
    setBits.push(`Unknown bits: ${bits.toString(2)}`);
  }
  return JSON.stringify(setBits);
}
